import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { OutboundReferenceCollection, ProductTransactionStatus } from '@gooduncles/gu-app-schema';
import { Button, DatePicker, Popconfirm, Tabs, message, notification } from 'antd';
import dayjs from 'dayjs';
import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { combineLatest, map } from 'rxjs';

import { errorObjectToString } from 'src/utils/common';
import { formatDate, getDefaultDeliveryDate } from 'src/utils/date-util';
import {
  batchEnd,
  batchStart,
  observeDailyInventoryChanges,
  observeInventoryBinLocation,
  observeProductInventories,
  observeProductOutboundTransaction,
  observeProductSupplier,
  observePurchaseOrderSectionConfig,
  updateProductInventoryForFieldBatch,
} from 'src/utils/firebase/firebase-short-cut';
import { ProcessedOutboundTransaction, processOutboundTransactions } from 'src/utils/transaction-util';

import { useTitle } from 'src/hooks/useTitle';

import DailyOutboundTransactionTable from 'src/atomic-components/organisms/Tables/DailyOutboundTransactionTable';
import DailyOutboundTransactionTableForPrint from 'src/atomic-components/organisms/Tables/DailyOutboundTransactionTableForPrint';
import GarakInboundCheckForPrint from 'src/atomic-components/organisms/Tables/GarakInboundCheckForPrint';
import PurchaseManagementTable from 'src/atomic-components/organisms/Tables/PurchaseManagementTable';

const defaultValue = getDefaultDeliveryDate();

const updateInventoryCost = async (selectedRows: ProcessedOutboundTransaction[]) => {
  if (selectedRows.length === 0) {
    message.error('신규 가격을 반영할 재고를 선택해주세요.');
    return;
  }
  try {
    batchStart();
    for (const row of selectedRows) {
      const { inventory, ea, bundle } = row;
      if (!inventory) {
        message.error(`재고 정보가 없는 행(${row.inventoryId})이 있습니다. 이 행은 건너뜁니다.`);
        continue;
      }
      const newEaCost = ea?.cost;
      const newBundleCost = bundle?.cost;
      const updateData = {
        ...(newEaCost ? { ['uom.ea.cost']: newEaCost } : {}),
        ...(newBundleCost ? { ['uom.bundle.cost']: newBundleCost } : {}),
      };
      await updateProductInventoryForFieldBatch(inventory._id, updateData);
    }
    await batchEnd();
    message.success('신규 가격 반영 완료');
  } catch (error) {
    console.error(error);
    const description = errorObjectToString(error);
    notification.error({
      message: '신규 가격 반영 실패',
      description,
    });
  }
};

const PurchaseOrderDailyOutboundTxn: FC = () => {
  useTitle('발주 관리 | 일일 출고 현황');
  const navigate = useNavigate();
  const { tab } = useParams();
  const [date, setDate] = useState<dayjs.Dayjs | null>(defaultValue);
  const [rowData, setRowData] = useState<ProcessedOutboundTransaction[]>([]);
  const [selectedRows, setSelectedRows] = useState<ProcessedOutboundTransaction[]>([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const dateForWhere = useMemo(() => formatDate(date ? date?.toDate() : new Date(), 'yyyy-MM-dd'), [date]);
  const disableAddADay = useMemo(() => (date?.unix() ? date?.unix() >= Date.now() / 1000 : false), [date]);
  const addADay = () => setDate((prev) => (prev ? prev.add(1, 'day') : null));
  const subADay = () => setDate((prev) => (prev ? prev.subtract(1, 'day') : null));

  const onTabChange = (key: string) => {
    navigate(`../daily-outbound-transaction/${key}`);
  };

  const handleUpdateInventoryCost = async () => {
    setIsUpdating(true);
    await updateInventoryCost(selectedRows);
    setIsUpdating(false);
  };

  useEffect(() => {
    const inventoryBinLocationObservable = observeInventoryBinLocation();
    const productSupplierObservable = observeProductSupplier();
    const productInventoryObservable = observeProductInventories();
    const productOutboundTransactionObservable = observeProductOutboundTransaction([
      ['requestDate', '==', dateForWhere],
      ['status', '==', ProductTransactionStatus.COMPLETED],
    ]).pipe(
      map((txns) => txns.filter((txn) => txn.referenceCollection !== OutboundReferenceCollection.INVENTORY_ISSUE)),
    );
    const dailyInventoryChangesObservable = observeDailyInventoryChanges(dateForWhere);
    // 선주문 설정 조회 (referenceCollection이 ORDER이고 활성화된 것만)
    const preOrderSectionConfigObservable = observePurchaseOrderSectionConfig([
      ['referenceCollection', '==', OutboundReferenceCollection.ORDER],
      ['enabled', '==', true],
    ]);
    const subscription = combineLatest([
      productOutboundTransactionObservable,
      productInventoryObservable,
      productSupplierObservable,
      inventoryBinLocationObservable,
      dailyInventoryChangesObservable,
      preOrderSectionConfigObservable,
    ]).subscribe(([txns, inventories, productSuppliers, binLocationConf, dailyInventoryChanges, preOrderConfigs]) => {
      // 선주문 설정에 포함된 모든 inventoryIds를 Set으로 모음
      const preOrderInventoryIds = new Set(preOrderConfigs.flatMap((config) => config.inventoryIds));
      const processedData = processOutboundTransactions(
        txns,
        inventories,
        productSuppliers,
        binLocationConf?.inventoryBinLocation,
        dailyInventoryChanges?.changes,
        preOrderInventoryIds,
      );
      setRowData(processedData);
    });
    return () => subscription.unsubscribe();
  }, [dateForWhere]);

  return (
    <div className='flexColumn height100'>
      <div className='flexRow flexAlignCenter flexSpaceBetween'>
        <div className='flexRow flexAlignCenter'>
          <Button icon={<LeftOutlined />} onClick={subADay} />
          <DatePicker onChange={setDate} defaultValue={defaultValue} value={date} />
          <Button icon={<RightOutlined />} onClick={addADay} disabled={disableAddADay} />
        </div>
        {(tab === 'table' || tab === 'purchase') && (
          <Popconfirm
            title={`신규 매입가를 현재 매입가에 적용합니다.`}
            onConfirm={handleUpdateInventoryCost}
            cancelButtonProps={{ disabled: isUpdating }}
            okButtonProps={{ loading: isUpdating }}
          >
            <Button disabled={selectedRows.length === 0} loading={isUpdating}>
              {selectedRows.length}건의 신규 가격 반영
            </Button>
          </Popconfirm>
        )}
      </div>
      <Tabs
        type='card'
        accessKey={tab}
        style={{ height: '100%', width: '100%', overflow: 'scroll' }}
        tabBarStyle={{ marginBottom: 0 }}
        onChange={onTabChange}
        items={[
          {
            key: 'table',
            label: '전체',
            children: (
              <DailyOutboundTransactionTable date={dateForWhere} rowData={rowData} setSelectedRows={setSelectedRows} />
            ),
          },
          {
            key: 'purchase',
            label: '매입처별',
            children: (
              <PurchaseManagementTable date={dateForWhere} rowData={rowData} setSelectedRows={setSelectedRows} />
            ),
          },
          {
            key: 'print',
            label: '프린트',
            children: <DailyOutboundTransactionTableForPrint rowData={rowData} />,
          },
          {
            key: 'garak-inbound',
            label: '입고확인(가락)',
            children: <GarakInboundCheckForPrint rowData={rowData} />,
          },
        ]}
      />
    </div>
  );
};

export default PurchaseOrderDailyOutboundTxn;
